import { cn } from '@/lib/utils'

/**
 * A Material Symbols Outlined glyph, by ligature name — the one icon set.
 *
 * `name` is the symbol's ligature (`search`, `filter_list`, `check_circle`).
 * It is drawn from `data-icon` by CSS generated content rather than written
 * as a text node, so the ligature's name never leaks into the page's text: not
 * into a copied selection, not into find-in-page, not into a translation, and
 * not into the text a test reads. Sizes are the design system's four:
 * 16 inside buttons, chips, badges and table cells; 20 by default; 24 in page
 * headers; 48 in empty and error states.
 *
 * Decorative by default (`aria-hidden`). An icon that carries meaning on its
 * own belongs inside a control with an `aria-label`, or next to a word — an
 * icon is never the only signal.
 *
 * It colours with `currentColor`, so `className="text-muted-foreground"` is
 * how to tint it. The styles live in `globals.css` under `.icon`.
 */
export function Icon({
  name,
  size = 20,
  filled = false,
  className,
}: {
  name: string
  size?: 16 | 20 | 24 | 48
  filled?: boolean
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      data-slot="icon"
      data-icon={name}
      data-size={size}
      data-filled={filled ? '' : undefined}
      className={cn('icon', className)}
    />
  )
}
